/**
 * SalesOrderListView — /sales  판매 현황(실데이터, 백엔드 V장 "판매 공사" A · A · A · A, 2026-11-02)
 *
 * 판매(sales_order) 한 건 = 한 줄 — # · 견적 번호 · 고객 · 디스크립션(품목 앞 3줄) · 상태 칩 · 판매 · 원가 · 마진(%) · 합산 마진 · 부족 · 담당자 · 등록일 · 버튼.
 * 판매 · 원가 · 마진은 품목 줄마다 쌓는다(원가 · 마진은 확정일 때만, 아니면 —). 합산 마진은 모든 품목이 확정일 때만(2026-11-02 목록 꼴 다시).
 * 탭 = 진행 중(판매 등록 · 출고 요청 · 세팅 완료, 기본) · 상태 여섯 · 전부. 찾기 = 견적 번호 · 고객 · 파트넘버(서버가 거른다).
 * 품목이 3줄을 넘으면 행 끝(버튼 자리)에 [외 n건 ▾] — 누르면 그 줄이 칸째 늘어나 품목 전부(상세 GET /:id)를 쌓고, 행 끝은 [접기 ▴] + 버튼.
 * 3줄 이하면 행 끝에 버튼(2026-11-03 — 옛 셋째 줄 끝 칩 · 펼친 자리 아래 줄을 옮김).
 * 버튼은 [출고 요청] · [취소] 둘 — [취소] 는 취소 창(사유 · 다시 등록할 품목)을 열고, 체크한 품목이 있으면 취소 뒤 판매 등록 화면으로 간다(2026-11-02 "취소 하나로").
 * 줄을 누르면 판매 상세(새 탭 — 품목 고치기 · 추가 · 딜 연결 · 이력, 2026-11-03). 버튼 · 링크 · 긁어 복사는 안 연다(제품 목록과 같은 꼴).
 * 견적 번호 아래 이어진 딜 번호(딜 페이지 새 탭) — 딜이 없으면 "딜 없음".
 * 버튼은 서버가 마지막에 막는다(409 글은 맨 위 한 줄).
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type {
  ESalesOrderFilter, SalesOrderDetail, SalesOrderItemHead, SalesOrderListQuery, SalesOrderView, SalesOrderViewList,
} from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import { openSalesCancelDialog } from '../components/sales-cancel-dialog';
import { assert } from '../lib/assert';
import { refreshKeepingFocus } from '../lib/keep-focus';
import { hasTextSelection } from '../lib/selection';
import { escapeHtml, fmtNumber } from '../lib/cells';
import { CLOSED_REASON_WORD, SALES_ORDER_FILTER_TABS, SALES_ORDER_STATUS_WORD, marginRateOf, shortageWordsOf } from './sales-order/words';
import {
  CANCELLABLE, CREATE_HREF, DISPATCH_CONFIRM_TEXT, DISPATCH_UNITS_CONFIRM_TEXT,
  DISPATCH_REQUEST_CANCEL_TEXT, SALE_CONFIRM_TEXT, SALE_UNCONFIRM_TEXT,
  SETUP_DONE_CONFIRM_TEXT, SHORTAGE_STATUSES, renderBackButton, renderStepButton, reregisterHrefOf,
} from './sales-order/actions';

interface SalesOrderListData {
  query: SalesOrderListQuery;               // 탭 · 찾기 — 서버가 거른다
  list: SalesOrderViewList | null;          // 받은 묶음. null = 아직
  loading: boolean;
  error: string;
  openId: string | null;                    // 펼친 판매(3줄 넘는 것) 또는 방금 만든 판매(줄 배경만). null = 없음
  detail: SalesOrderDetail | null;          // 펼친 판매의 상세. null = 받는 중
  detailError: string;                      // 펼친 판매의 상세 받기 실패 글
  busy: boolean;                            // 버튼 누르는 중 — 버튼 셋을 잠근다
  notice: string;                           // 버튼 뒤 알림 한 줄 — '' = 없음. 탭 · 찾기 · 다른 줄 펼침 때 지운다
  actionError: string;                      // 버튼 실패 글(409 등) — 맨 위 한 줄. '' = 없음
}

const TAB_HREF = '/sales';
const QUERY_DEBOUNCE_MS = 300;
const COLUMN_COUNT = 14;                    // 표 열 수 — 빈 줄 · 펼친 자리 아래의 colspan
const ITEM_HEAD_COUNT = 3;                  // 한 줄에 보이는 품목 수 — 넘으면 "외 n건 ▾"(백엔드 items_head 와 같다)
const LEFT_TAB_TEXT = ' · 이 탭에서 빠졌습니다';

/** 계산서 칸 — 발행했으면 초록 O(호버에 발행일), 아직이면 회색 X(2026-11-03 "O X 처럼 가볍게" · 회의 10-21 §9) */
function renderTaxInvoiceMark(dateOrNull: string | null): string {
  if (dateOrNull === null) return '<span class="so-tax-no" title="세금계산서 미발행">X</span>';
  return `<span class="so-tax-yes" title="세금계산서 발행 ${escapeHtml(dateOrNull)}">O</span>`;
}

/** 줄 클릭(판매 상세 열기)이 비켜 가는 자리 — 링크 · 버튼 · [세팅 완료 ▾] 메뉴(<details> — ▾ 와 메뉴 빈 곳은 버튼이 아니라 줄로 번졌다, 2026-11-04) */
const ROW_CLICK_SKIP = 'a, button, details';

export class SalesOrderListView extends SolaView<SalesOrderListData> {
  override events = {
    click: {
      '@filter-tab': 'handleFilterTab',
      '@confirm-sale': 'handleConfirmSale',
      '@unconfirm-sale': 'handleUnconfirmSale',
      '@cancel-dispatch-request': 'handleCancelDispatchRequest',
      '@request-dispatch': 'handleRequestDispatch',
      '@setup-done': 'handleSetupDone',
      '@dispatch': 'handleDispatch',
      '@cancel': 'handleCancel',
      '@more': 'handleMore',
      '@open': 'handleOpen',
    },
    input: { '@query': 'handleQuery' },
  };

  private queryTimer: ReturnType<typeof setTimeout> | null = null;

  /** openIdOrNull — 견적 없는 판매 등록이 돌아올 때 넘기는 방금 만든 판매(`/sales?open=<id>`). 있으면 탭 '전부' 로 열고 그 줄을 펼친다 */
  constructor(openIdOrNull: string | null) {
    super();
    this.data = {
      query: { filter: openIdOrNull === null ? 'open' : 'all', q: '' }, list: null, loading: true, error: '',
      openId: openIdOrNull, detail: null, detailError: '', busy: false, notice: '', actionError: '',
    };
  }

  override getTitle(): string {
    return '판매 현황 — HCLTech';
  }

  override onMount(): void {
    void this.load();
    if (this.data.openId !== null) void this.loadDetail(this.data.openId);
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF);
    assert(place !== null, `판매 현황 탭이 메뉴에 없다: ${TAB_HREF} — MENU_GROUPS 판매 › 판매 현황의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-sales-order pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  /** 본문(.pv-body)을 다시 그린다 — 찾기 칸에 치던 포커스 · 커서는 지킨다(lib/keep-focus) */
  private refreshBody(): void {
    refreshKeepingFocus(this._rootElement ?? null, '.pv-body', () => this.refreshRegion('.pv-body', () => this.renderBody()));
  }

  // ─── 불러오기 ───
  private async load(): Promise<void> {
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody();
    try {
      const list = await api.fetchSalesOrders(this.data.query);
      this.data = { ...this.data, list, loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody();
  }

  /** 펼친 판매 한 건 — 받는 사이 다른 줄을 펼쳤으면 늦게 온 답은 버린다 */
  private async loadDetail(id: string): Promise<void> {
    try {
      const detail = await api.fetchSalesOrder(id);
      if (this.data.openId !== id) return;
      this.data = { ...this.data, detail };
    } catch (e) {
      if (this.data.openId !== id) return;
      this.data = { ...this.data, detailError: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody();
  }

  /** 버튼 둘이 같이 — 잠그고 부른 뒤 목록을 다시 받는다. 펼친 판매였으면 받은 상세로 바꾼다. 실패 글은 맨 위 한 줄. 됐으면 true */
  private async runAction(id: string, act: (saleId: string) => Promise<SalesOrderDetail>): Promise<boolean> {
    this.data = { ...this.data, busy: true, actionError: '', notice: '' };
    this.refreshBody();
    try {
      const detail = await act(id);
      const bOpen = this.data.openId === id;
      this.data = { ...this.data, busy: false, detail: bOpen ? detail : this.data.detail, notice: `${detail.quote_no} — ${SALES_ORDER_STATUS_WORD[detail.status]}` };
      await this.load();
      this.closeIfLeftTab(id);
      return true;
    } catch (e) {
      this.data = { ...this.data, busy: false, actionError: e instanceof Error ? e.message : String(e) };
      this.refreshBody();
      return false;
    }
  }

  /** 버튼 뒤 다시 받은 목록에 그 판매가 없으면(탭에서 빠짐) 알림에 그렇게 적고, 펼친 판매였으면 닫는다 */
  private closeIfLeftTab(id: string): void {
    const rows = this.data.list === null ? [] : this.data.list.rows;
    if (rows.some((view) => view.sales_order_id === id)) return;
    const bOpen = this.data.openId === id;
    this.data = {
      ...this.data,
      openId: bOpen ? null : this.data.openId, detail: bOpen ? null : this.data.detail, detailError: bOpen ? '' : this.data.detailError,
      notice: `${this.data.notice}${LEFT_TAB_TEXT}`,
    };
    this.refreshBody();
  }

  // ─── 그리기 ───
  private renderBody(): string {
    const rows = this.data.list === null ? [] : this.data.list.rows;
    return `
      ${this.renderToolbar()}
      ${this.data.notice === '' ? '' : `<p class="so-notice">${escapeHtml(this.data.notice)}</p>`}
      ${this.data.actionError === '' ? '' : `<p class="so-notice so-notice-error">${escapeHtml(this.data.actionError)}</p>`}
      <table class="pv-table so-table">
        <thead><tr>${ROW_NO_HEAD}<th>견적 번호</th><th>고객</th><th>디스크립션</th><th>상태</th><th>판매</th><th>원가</th><th>마진</th><th title="모든 품목의 원가가 확정일 때만">합산 마진</th><th title="아직 실물이 안 묶인 수량">부족</th><th>담당자</th><th>등록일</th><th class="so-tax-head" title="세금계산서 — O 발행 · X 미발행(발행일은 판매 상세에서 적는다)">계산서</th><th></th></tr></thead>
        <tbody>${this.renderRows(rows)}</tbody>
      </table>`;
  }

  /** 도구 줄 — 찾기 · 탭 · [판매 등록](재고 목록과 같은 순서 — 찾기가 맨 앞) */
  private renderToolbar(): string {
    const { query } = this.data;
    const tabs = SALES_ORDER_FILTER_TABS.map((tab) => `<button type="button" class="so-tab${tab.filter === query.filter ? ' active' : ''}" data-action="filter-tab" data-filter="${tab.filter}">${tab.label}</button>`).join('');
    return `
      <div class="pc-filter-bar">
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="query" placeholder="견적 번호 · 고객 · 파트넘버" value="${escapeHtml(query.q)}">
        </label>
        <div class="so-tabs">${tabs}</div>
        <span class="pv-spacer"></span>
        <a class="btn-primary so-create" data-link href="${CREATE_HREF}">판매 등록</a>
      </div>`;
  }

  private renderRows(rows: readonly SalesOrderView[]): string {
    const { loading, error } = this.data;
    if (error !== '') return `<tr><td colspan="${COLUMN_COUNT}" class="text-danger">불러오지 못했습니다 — ${escapeHtml(error)}</td></tr>`;
    if (loading && rows.length === 0) return `<tr><td colspan="${COLUMN_COUNT}" class="text-muted">불러오는 중…</td></tr>`;
    if (rows.length === 0) return `<tr><td colspan="${COLUMN_COUNT}" class="text-muted">맞는 판매가 없습니다</td></tr>`;
    return rows.map((view, index) => this.renderRow(view, index)).join('');
  }

  /** 판매 한 건 = 한 줄. 판매 · 원가 · 마진은 품목 줄마다 쌓는다. 3줄 이하면 버튼은 행 끝, 넘으면 펼친 자리 아래 */
  private renderRow(view: SalesOrderView, rowNo: number): string {
    const bOpen = this.data.openId === view.sales_order_id;
    const bMany = view.item_count > ITEM_HEAD_COUNT;
    const lines = this.linesOf(view);
    return `
      <tr class="pl-row so-row${bOpen ? ' so-row-open' : ''}" data-action="open" data-href="/sales/${escapeHtml(view.sales_order_id)}" title="누르면 판매 상세(새 탭)">
        ${rowNoCell(0, rowNo)}
        <td class="pl-sub mono so-no">${escapeHtml(view.quote_no)}${renderDealCell(view)}</td>
        <td class="pl-main so-customer-cell" title="${escapeHtml(view.customer_name ?? '')}">${textOrDash(view.customer_name)}</td>
        <td class="pl-main so-items-cell">${renderItemLines(lines)}</td>
        <td><span class="so-status so-status-${view.status}" title="${closedReasonTitle(view)}">${SALES_ORDER_STATUS_WORD[view.status]}</span></td>
        <td class="pl-qty so-stack-cell">${stackOf(lines, (line) => wonText(line.sale_amount))}</td>
        <td class="pl-qty so-stack-cell">${stackOf(lines, (line) => wonText(line.cost_amount))}</td>
        <td class="pl-qty so-stack-cell">${stackOf(lines, marginText)}</td>
        <td class="pl-qty so-margin-cell">${marginText(view)}</td>
        <td class="pl-qty${isShortageShown(view) ? ' so-short' : ''}" title="${isShortageShown(view) ? escapeHtml(shortageWordsOf(view.shortage_total, view.purchase_needed_total)) : ''}">${isShortageShown(view) ? fmtNumber(view.shortage_total) : '—'}</td>
        <td class="pl-sub">${textOrDash(view.manager_name)}</td>
        <td class="pl-sub">${escapeHtml(view.created_at.slice(0, 10))}</td>
        <td class="so-tax-cell">${renderTaxInvoiceMark(view.tax_invoice_date)}</td>
        <td class="so-actions-cell">${this.renderActionCell(view, bMany)}</td>
      </tr>
`;
  }

  /** 쌓을 줄 — 펼쳤고 상세가 왔으면 품목 전부(detail.items), 아니면 앞 3줄(items_head). 둘 다 SalesOrderItemHead 꼴 */
  private linesOf(view: SalesOrderView): readonly SalesOrderItemHead[] {
    const { openId, detail } = this.data;
    const bDetailHere = openId === view.sales_order_id && detail !== null && detail.sales_order_id === view.sales_order_id;
    return bDetailHere ? detail.items : view.items_head;
  }

  /** 행 끝 칸 — 3줄 이하면 버튼. 넘으면 접힘 [외 n건 ▾] · 펼침 [접기 ▴] + 버튼(받는 중 · 실패 글은 그 아래) */
  private renderActionCell(view: SalesOrderView, bMany: boolean): string {
    if (!bMany) return this.renderActions(view);
    const id = escapeHtml(view.sales_order_id);
    const rest = view.item_count - ITEM_HEAD_COUNT;
    if (this.data.openId !== view.sales_order_id) {
      return `<button type="button" class="btn-ghost so-more" data-action="more" data-id="${id}">외 ${fmtNumber(rest)}건 ▾</button>`;
    }
    const { detail, detailError } = this.data;
    const loading = detail === null && detailError === '' ? '<span class="text-muted so-more-note">불러오는 중…</span>' : '';
    const error = detailError === '' ? '' : `<span class="text-danger so-more-note">${escapeHtml(detailError)}</span>`;
    return `
      <div class="so-more-open">
        <button type="button" class="btn-ghost so-more" data-action="more" data-id="${id}">접기 ▴</button>
        ${this.renderActions(view)}
        ${loading}${error}
      </div>`;
  }

  /** 버튼 — 다음 단계 하나(renderStepButton — 부족이 0 일 때만) + 한 칸 되돌리기(renderBackButton) + [취소]. 버튼마다 data-id(판매 id) */
  private renderActions(view: SalesOrderView): string {
    const disabled = this.data.busy ? ' disabled' : '';
    const idAttr = ` data-id="${escapeHtml(view.sales_order_id)}"`;
    const step = view.shortage_total === 0 ? renderStepButton(view.status, idAttr, disabled) : '';
    const bCancel = CANCELLABLE.includes(view.status);
    return `
      <div class="so-actions">
        ${step}
        ${renderBackButton(view.status, idAttr, disabled)}
        ${bCancel ? `<button type="button" class="btn-ghost so-danger" data-action="cancel"${idAttr}${disabled}>취소</button>` : ''}
      </div>`;
  }

  // ─── 핸들러 ───
  handleFilterTab(_e: Event, el: HTMLElement): void {
    const filter = filterOf(el.dataset.filter);
    this.data = { ...this.data, query: { ...this.data.query, filter }, openId: null, detail: null, detailError: '', notice: '', actionError: '' };
    void this.load();
  }

  handleQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, q: (el as HTMLInputElement).value }, notice: '', actionError: '' };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.load(); }, QUERY_DEBOUNCE_MS);
  }

  /** "외 n건 ▾" 칩 — 펼침 · 같은 판매면 접기. 다른 판매를 펼치면 앞 것은 접힌다 */
  handleMore(_e: Event, el: HTMLElement): void {
    const id = saleIdOf(el);
    if (this.data.openId === id) {
      this.data = { ...this.data, openId: null, detail: null, detailError: '' };
      this.refreshBody();
      return;
    }
    this.data = { ...this.data, openId: id, detail: null, detailError: '', notice: '' };
    this.refreshBody();
    void this.loadDetail(id);
  }

  /** 줄 클릭 → 판매 상세(새 탭). 버튼 · 링크는 그쪽이 받고, 긁어 복사(드래그)면 안 연다 */
  handleOpen(e: Event, el: HTMLElement): void {
    if ((e.target as HTMLElement).closest(ROW_CLICK_SKIP) !== null) return;
    if (hasTextSelection()) return;
    const href = el.dataset.href;
    assert(href !== undefined, '줄은 data-href(판매 상세 주소)를 가진다 — renderRow 가 붙였다');
    window.open(href, '_blank', 'noopener');
  }

  async handleConfirmSale(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    if (!confirm(SALE_CONFIRM_TEXT)) return;
    await this.runAction(id, api.confirmSalesOrder);
  }

  async handleUnconfirmSale(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    if (!confirm(SALE_UNCONFIRM_TEXT)) return;
    await this.runAction(id, api.unconfirmSalesOrder);
  }

  async handleCancelDispatchRequest(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    if (!confirm(DISPATCH_REQUEST_CANCEL_TEXT)) return;
    await this.runAction(id, api.cancelSalesOrderDispatchRequest);
  }

  async handleRequestDispatch(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    if (!confirm(DISPATCH_CONFIRM_TEXT)) return;
    await this.runAction(id, api.requestSalesOrderDispatch);
  }

  async handleSetupDone(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    if (!confirm(SETUP_DONE_CONFIRM_TEXT)) return;
    await this.runAction(id, api.completeSalesOrderSetup);
  }

  async handleDispatch(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    if (!confirm(DISPATCH_UNITS_CONFIRM_TEXT)) return;
    await this.runAction(id, api.dispatchSalesOrder);
  }

  /** [취소] — 상세(품목 행 id)를 받아 취소 창을 열고, 취소가 되면 체크한 품목을 들고 판매 등록 화면으로 */
  async handleCancel(_e: Event, el: HTMLElement): Promise<void> {
    const id = saleIdOf(el);
    let detail: SalesOrderDetail;
    try {
      detail = await api.fetchSalesOrder(id);
    } catch (e) {
      this.data = { ...this.data, actionError: e instanceof Error ? e.message : String(e), notice: '' };
      this.refreshBody();
      return;
    }
    const choice = await openSalesCancelDialog(detail);
    if (choice === null) return;
    const bDone = await this.runAction(id, (saleId) => api.cancelSalesOrder(saleId, choice.input));
    if (bDone && choice.reregister_item_ids.length > 0) {
      this.navigateTo(reregisterHrefOf(id, choice.reregister_item_ids));
    }
  }

  private navigateTo(path: string): void {
    // SolaRouter 의 a[data-link] 인터셉트와 동일 동작 — history.pushState
    history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

/** 견적 번호 아래 한 줄 — 이어진 딜 번호(새 탭) · 딜이 없으면 "딜 없음"(연결은 판매 상세에서) */
function renderDealCell(view: SalesOrderView): string {
  if (view.deal_id === null) return '<span class="so-deal-link">딜 없음</span>';
  return `<a class="so-deal-link" href="/deal/${escapeHtml(view.deal_id)}" target="_blank" rel="noopener">${escapeHtml(view.deal_no ?? `딜 ${view.deal_id}`)}</a>`;
}

/** 디스크립션 칸 — 줄마다 파트넘버 · 디스크립션 · ×수량 · 컨디션 */
function renderItemLines(lines: readonly SalesOrderItemHead[]): string {
  return lines.map((line) => {
    return `
      <div class="so-item-line" title="${escapeHtml(line.description ?? '')}">
        ${line.option_part === null || line.option_part === '' ? '' : `<span class="so-item-pn mono">${escapeHtml(line.option_part)}</span>`}
        <span class="so-item-name">${escapeHtml(line.description ?? '')}</span>
        <span class="so-item-qty">×${fmtNumber(line.quantity)}${line.condition === null ? '' : ` · ${escapeHtml(line.condition)}`}</span>
      </div>`;
  }).join('');
}

/** 판매 · 원가 · 마진 칸 — 품목 줄에 맞춰 한 줄씩 쌓는다 */
function stackOf(lines: readonly SalesOrderItemHead[], cellOf: (line: SalesOrderItemHead) => string): string {
  return lines.map((line) => `<div class="so-stack-line">${cellOf(line)}</div>`).join('');
}

/** 상태 칩 title — 취소 · 취소(일부 출고)면 "취소 사유 — 사유 · 글", 아니면 '' */
function closedReasonTitle(view: SalesOrderView): string {
  if (view.closed_reason === null) return '';
  const note = view.closed_note === null ? '' : ` · ${view.closed_note}`;
  return escapeHtml(`취소 사유 — ${CLOSED_REASON_WORD[view.closed_reason]}${note}`);
}

/** 버튼 · 칩의 data-id → 판매 id. 우리가 그린 것만 오니 없으면 있을 수 없는 상태 */
function saleIdOf(el: HTMLElement): string {
  const id = el.dataset.id;
  assert(id !== undefined, '버튼 · 칩은 data-id(판매 id)를 가진다 — renderActions · renderActionCell 이 붙였다');
  return id;
}

/** 부족 칸에 숫자를 보이나 — 진행 중 상태이고 부족이 있을 때만 */
function isShortageShown(view: SalesOrderView): boolean {
  return SHORTAGE_STATUSES.includes(view.status) && view.shortage_total > 0;
}

/** 금액 글 — null 이면 "—" */
function wonText(amount: number | null): string {
  return amount === null ? '—' : fmtNumber(Math.round(amount));
}

/** 글 칸 — null 이면 흐린 "—" */
function textOrDash(text: string | null): string {
  return text === null ? '<span class="text-muted">—</span>' : escapeHtml(text);
}

/** 마진 + 마진율 " (21%)" — 음수는 붉게 */
function marginText(amounts: { cost_amount: number | null; margin_amount: number | null }): string {
  const rate = marginRateOf(amounts);
  const negative = amounts.margin_amount !== null && amounts.margin_amount < 0 ? ' class="so-negative"' : '';
  return `<span${negative}>${wonText(amounts.margin_amount)}</span>${rate === null ? '' : ` <span class="text-muted">(${rate.toFixed(0)}%)</span>`}`;
}

/** 탭 data-filter → 탭 값. 우리가 그린 값만 오니 밖이면 있을 수 없는 상태 */
function filterOf(value: string | undefined): ESalesOrderFilter {
  const filter = SALES_ORDER_FILTER_TABS.find((tab) => tab.filter === value);
  assert(filter !== undefined, `탭 data-filter '${String(value)}' 가 SALES_ORDER_FILTER_TABS 밖이다 — renderToolbar 가 그린 값만 온다`);
  return filter.filter;
}
